import { useQuery } from '@tanstack/react-query';

import { getAffiliateProfile } from '../../actions';
import { Currency } from '../../lib/enum';
import { AffiliateProfileData, GetAffiliateProfileResponse } from '../../lib/types';
import { getCurrencyFromCountry } from '../../lib/utils';

interface UseAffiliateProfileProps {
  affiliateId: string;
}

interface ProcessedAffiliateProfile {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  country: string;
  currency: Currency;
  status: string;
  balance: number;
  totalEarned: number;
  totalUsersInvited: number;
  codeName: string;
  createdAt: string;
  updatedAt: string;
}

const useAffiliateProfile = ({ affiliateId: _affiliateId }: UseAffiliateProfileProps) => {
  const { data, isInitialLoading, isFetched, refetch } = useQuery<unknown, Error, GetAffiliateProfileResponse>(
    ['/affiliate/v2/portal/affiliate-profile'],
    getAffiliateProfile,
    {
      // The endpoint resolves the affiliate from the bearer token, so the id isn't in
      // the key — but there's no session to authenticate with until we have one.
      enabled: Boolean(_affiliateId),
      suspense: false,
      keepPreviousData: true
    }
  );

  const processProfile = (): ProcessedAffiliateProfile | null => {
    // Handle both response structures: { body: {...} } or direct body
    const profileData = (data as GetAffiliateProfileResponse)?.body || (data as unknown as AffiliateProfileData);
    if (!profileData) return null;

    const profile = profileData;
    const currency = getCurrencyFromCountry(profile.country);
    
    // Get codeName from the first affiliate coupon's coupon code
    const codeName = profile.affiliateCoupons?.[0]?.couponDetails?.couponCode || '';

    return {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      phoneNumber: profile.phoneNumber,
      country: profile.country,
      currency,
      status: profile.status,
      balance: profile.balance,
      totalEarned: profile.totalEarned,
      totalUsersInvited: profile.totalUsersInvited,
      codeName,
      createdAt: profile.createdAt,
      updatedAt: profile.updatedAt
    };
  };

  const processedProfile = processProfile();
  // Extract affiliateCoupons - handle both response structures
  const profileData = (data as GetAffiliateProfileResponse)?.body || (data as unknown as AffiliateProfileData);
  const affiliateCoupons = profileData?.affiliateCoupons || [];

  return {
    profile: processedProfile,
    affiliateCoupons,
    profileLoading: isInitialLoading,
    // True once the query has settled at least once, success or failure. Unlike
    // isInitialLoading/isError it doesn't flip back when a retry starts, so callers
    // can gate a subtree on it without mount/unmount churn.
    profileFetched: isFetched,
    refetchProfile: refetch
  };
};

export default useAffiliateProfile;

